/**
 * EU Event Routing Configuration
 *
 * Events listed here will route API calls and Cognito auth
 * to EU (eu-central-1) endpoints instead of the default ca-central-1 endpoints.
 *
 * To add a new EU event:
 *   1. Add the event identifier (lowercase) to EU_EVENT_IDS
 *   2. Update EU_DEV_ENDPOINTS / EU_PROD_ENDPOINTS if the API Gateway IDs change
 */

import { environment } from 'src/environments/environment';

export interface EuEndpoints {
  apiUrl: string;
  awsRegion: string;
  xApiKey: string;
  userPoolId: string;
  userPoolClientId: string;
}

/**
 * Event identifiers that should route to EU services.
 * Use lowercase route param values (e.g., 'wcs2026' for /reports/wcs2026).
 *
 * Empty by default — reports API is region-agnostic (ca-central-1 prod build).
 * For EU-only events, add slugs here (mirror synopsis-event-insights eu-event-routing.config.ts)
 * or deploy with environment.eu-prod.ts file replacement.
 */
export const EU_EVENT_IDS: Set<string> = new Set(['wcs2026']);

/** EU Dev (eu-central-1, eu-dev stage) */
const EU_DEV_ENDPOINTS: EuEndpoints = {
  apiUrl: 'https://eu-dev.api.synopsis.rozie.ai',
  awsRegion: 'eu-central-1',
  xApiKey: 'Yqa7AKeRCp7ttQxHeeNa32327Hnv9Llw9LtgE8of',
  userPoolId: 'eu-central-1_lNm5hfqJJ',
  userPoolClientId: '5b9iv2k2931q5e0jo5o5fc0gqh',
};

/** EU Prod (eu-central-1, eu-prod stage) */
const EU_PROD_ENDPOINTS: EuEndpoints = {
  apiUrl: 'https://eu.api.synopsis.rozie.ai',
  awsRegion: 'eu-central-1',
  xApiKey: 'fQkA6abZqr9WtLBbIziVD4DcVjFzd24g7y7bZbly',
  userPoolId: 'eu-central-1_IDDcfA0aO',
  userPoolClientId: '604c9hh975q1ohof9psdo0bse7',
};

/** Use prod endpoints for production builds, dev endpoints for dev builds */
export const EU_ENDPOINTS: EuEndpoints = environment.production
  ? EU_PROD_ENDPOINTS
  : EU_DEV_ENDPOINTS;

/**
 * Check if an event should use EU endpoints.
 */
export function isEuEvent(eventName: string): boolean {
  return EU_EVENT_IDS.has(eventName.toLowerCase());
}

/** Default endpoints captured at module load; restored when navigating away from EU events. */
const DEFAULT_ENDPOINTS: EuEndpoints = {
  apiUrl: environment.apiUrl,
  awsRegion: 'ca-central-1',
  xApiKey: environment.X_API_KEY,
  userPoolId: environment.COGNITO_USER_POOL_ID,
  userPoolClientId: environment.COGNITO_CLIENT_ID,
};

/**
 * Get EU endpoints if the event is EU-routed, otherwise null.
 */
export function getEuEndpointsForEvent(eventName: string): EuEndpoints | null {
  return isEuEvent(eventName) ? EU_ENDPOINTS : null;
}

/** Applies EU endpoint overrides for the route slug, or restores build-time defaults. */
export function applyEuEnvironmentForEvent(eventName: string): EuEndpoints {
  const endpoints = getEuEndpointsForEvent(eventName) ?? DEFAULT_ENDPOINTS;
  environment.apiUrl = endpoints.apiUrl;
  environment.X_API_KEY = endpoints.xApiKey;
  return endpoints;
}
